import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import {
  MatSlideToggleModule,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PanelContainerComponent,
  PanelHeaderComponent,
  PanelBodyComponent,
} from 'src/app/shared/content-panels';
import { SpinnerService } from 'src/app/shared/services';
import { EnergyCreditsAdminService } from './services/energy-credits-admin.service';
import { FacilityEnergyCredit } from './models/facility-energy-credits.model';

@Component({
  selector: 'app-energy-credits-admin-facilities',
  imports: [
    CommonModule,
    MatTableModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    PanelContainerComponent,
    PanelHeaderComponent,
    PanelBodyComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './energy-credits-admin-facilities.component.html',
  styleUrl: './energy-credits-admin-facilities.component.scss',
})
export class EnergyCreditsAdminFacilitiesComponent implements OnInit {
  private service = inject(EnergyCreditsAdminService);
  private spinner = inject(SpinnerService);
  private snackBar = inject(MatSnackBar);

  readonly displayedColumns = ['facilityNumber', 'name', 'status', 'toggle'];
  readonly dataSource = new MatTableDataSource<FacilityEnergyCredit>([]);
  readonly savingId = signal<string | null>(null);

  ngOnInit(): void {
    this.dataSource.filterPredicate = (facility, filter) => {
      const status = facility.IsEnergyCreditsEnabled ? 'enabled' : 'disabled';
      return `${facility.Name ?? ''} ${facility.KeyChain?.LegacyId ?? ''} ${status}`
        .toLowerCase()
        .includes(filter);
    };
    this.loadFacilities();
  }

  loadFacilities(): void {
    this.spinner.show();
    this.service.getFacilities().subscribe({
      next: (facilities) => {
        this.dataSource.data = [...facilities].sort((a, b) =>
          (a.Name ?? '').localeCompare(b.Name ?? ''),
        );
        this.spinner.hide();
      },
      error: () => {
        this.spinner.hide();
        this.snackBar.open('Failed to load facilities.', 'Dismiss', {
          duration: 7_000,
        });
      },
    });
  }

  applyFilter(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.dataSource.filter = value.trim().toLowerCase();
  }

  onRowClick(facility: FacilityEnergyCredit): void {
    this.persist(facility, !facility.IsEnergyCreditsEnabled);
  }

  onToggleChange(
    facility: FacilityEnergyCredit,
    change: MatSlideToggleChange,
  ): void {
    this.persist(facility, change.checked);
  }

  private persist(facility: FacilityEnergyCredit, newValue: boolean): void {
    // Ignore interactions for a row that is already mid-save.
    if (this.savingId() === facility.Id) {
      return;
    }
    const previous = facility.IsEnergyCreditsEnabled;
    // Optimistically reflect the new value; the [checked] binding updates the toggle.
    facility.IsEnergyCreditsEnabled = newValue;
    this.savingId.set(facility.Id);
    this.service.setEnergyCreditsEnabled(facility.Id, newValue).subscribe({
      next: (result) => {
        this.savingId.set(null);
        const update = result?.data?.UpdateFacilityEnergyCreditsEnabled;
        if (update?.Success) {
          this.snackBar.open(
            `${facility.Name} ${newValue ? 'enabled' : 'disabled'} for Energy Credits.`,
            'Dismiss',
            { duration: 3000 },
          );
        } else {
          // Revert on failure; the [checked] binding follows the property.
          facility.IsEnergyCreditsEnabled = previous;
          this.snackBar.open(
            update?.Message || 'Failed to update facility. Please try again.',
            'Dismiss',
            { duration: 5000 },
          );
        }
      },
      error: () => {
        this.savingId.set(null);
        facility.IsEnergyCreditsEnabled = previous;
        this.snackBar.open(
          'Failed to update facility. Please try again.',
          'Dismiss',
          {
            duration: 5000,
          },
        );
      },
    });
  }
}
